/**
 * Script & voice-config mutations.
 *
 * ALL NON-OPTIMISTIC: these are provider/voice configuration and script
 * approval — the two categories the policy calls out as never-optimistic. Each
 * waits for the backend and invalidates the affected config/version caches.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type HotelScript,
  hotelScriptSchema,
  normalizeVoiceConfig,
  type VoiceConfig,
  voiceConfigSchema,
} from '../responses';
import type {
  CreateOrUpdateScriptBody,
  SetFallbackLanguageBody,
  SetHandoffRulesBody,
} from './_types';

/** `PUT /scripts` — create or update a script draft for a (language, field). */
export function usePutScript(
  tenantId: string,
  hotelId: string,
): UseMutationResult<HotelScript, unknown, CreateOrUpdateScriptBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrUpdateScriptBody) => {
      const result = await client.PUT('/api/v1/tenants/{tenantId}/hotels/{hotelId}/scripts', {
        params: { path: { tenantId, hotelId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, hotelScriptSchema);
    },
    onSuccess: (script) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.voiceConfig(tenantId, hotelId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scriptVersions(tenantId, hotelId, {
          language: script.language,
          field: script.field,
        }),
      });
    },
  });
}

/** `POST /scripts/:scriptId/approve` — approve a script draft (records session approver). */
export function useApproveScript(
  tenantId: string,
  hotelId: string,
): UseMutationResult<HotelScript, unknown, { scriptId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scriptId }: { scriptId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/scripts/{scriptId}/approve',
        { params: { path: { tenantId, hotelId, scriptId } }, parseAs: 'json' },
      );
      return unwrap(result, hotelScriptSchema);
    },
    onSuccess: (script) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.voiceConfig(tenantId, hotelId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scriptVersions(tenantId, hotelId, {
          language: script.language,
          field: script.field,
        }),
      });
    },
  });
}

/** `PUT /voice-config/handoff-rules` — set handoff rules (escalation config). */
export function useSetHandoffRules(
  tenantId: string,
  hotelId: string,
): UseMutationResult<VoiceConfig, unknown, SetHandoffRulesBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetHandoffRulesBody) => {
      const result = await client.PUT(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/voice-config/handoff-rules',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      normalizeVoiceConfig(result.data);
      return unwrap(result, voiceConfigSchema);
    },
    onSuccess: (config) => {
      queryClient.setQueryData(queryKeys.voiceConfig(tenantId, hotelId), config);
    },
  });
}

/** `PUT /voice-config/fallback-language` — set the language calls fall back to (spec 47). */
export function useSetFallbackLanguage(
  tenantId: string,
  hotelId: string,
): UseMutationResult<VoiceConfig, unknown, SetFallbackLanguageBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetFallbackLanguageBody) => {
      const result = await client.PUT(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/voice-config/fallback-language',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      normalizeVoiceConfig(result.data);
      return unwrap(result, voiceConfigSchema);
    },
    onSuccess: (config) => {
      // Fallback changes the payload's language inheritance; refresh the whole
      // voice-config + every per-field version query so the studio stays honest.
      queryClient.setQueryData(queryKeys.voiceConfig(tenantId, hotelId), config);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scriptVersionsPrefix(tenantId, hotelId),
      });
    },
  });
}
